import { chipTextColor } from '../../utils/format';
import type { TaskLabel } from '../../services/api/board';

export interface TaskLabelChipsProps {
  labels: TaskLabel[] | undefined;
  /** Optional cap on how many chips render before the rest are elided. */
  max?: number;
}

/**
 * MKS-164 — presentational label chip row. Renders NOTHING for an empty (or
 * missing) array — a task with no labels shows no chip row at all, exactly
 * like every other optional card cluster (assignee avatar, tier badge).
 *
 * Colors come straight from the label's own hex, paired with
 * `chipTextColor()` for contrast — the same rule already used for the
 * tier badge on this card, so no second palette is introduced.
 */
export function TaskLabelChips({ labels, max }: TaskLabelChipsProps) {
  if (!labels || labels.length === 0) return null;
  const shown = max != null ? labels.slice(0, max) : labels;
  const hiddenCount = max != null ? labels.length - shown.length : 0;

  return (
    <div className="flex flex-wrap items-center gap-1 mt-1.5" data-testid="task-label-chips">
      {shown.map((label) => (
        <span
          key={label.id}
          title={label.name}
          data-testid={`task-label-chip-${label.id}`}
          style={{ backgroundColor: label.color, color: chipTextColor(label.color) }}
          className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none shrink-0"
        >
          {label.name}
        </span>
      ))}
      {hiddenCount > 0 && (
        <span className="text-[10px] text-ms-text-secondary shrink-0">+{hiddenCount}</span>
      )}
    </div>
  );
}
